import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AddIcon } from '@/components/ui/Icons';
import { Session } from '@/api/client';
import { DayCell as DayCellType } from '@/schedule/scheduleUtils';
import {
  MONTH_NAMES,
  DAY_NAMES,
  STUDIOS,
  TIME_SLOTS,
  STUDIO_COL_WIDTH,
  TIME_COLUMN_WIDTH,
  TOTAL_MINUTES,
  MONTAGE_GAP,
  CONTENT_HEIGHT,
} from '@/schedule/scheduleConstants';
import {
  parseTimeToMinutes,
  getStudioIndex,
  getSessionAtSlot,
  toDayIso,
  SLOT_HEIGHT,
  SESSION_DRAG_TYPE,
} from '@/schedule/scheduleUtils';
import { scheduleDragState } from '@/schedule/scheduleDragState';
import { getSessionDropEffect, hasSlotSelectModifier } from '@/schedule/scheduleModifierKeys';
import { useScheduleHandlers, useScheduleSelection } from '@/contexts/ScheduleContext';
import type { ScheduleSearchFilterState } from '@/schedule/scheduleSearchFilter';
import { SessionBubble } from './SessionBubble';
import './DayCell.css';

const COL_WIDTH = STUDIO_COL_WIDTH;
const SESSION_VISUAL_WIDTH_RATIO = 0.95;

const SCHEDULE_SEARCH_INACTIVE: ScheduleSearchFilterState = {
  active: false,
  words: [],
  matchIds: null,
};

function getSlotLeft(studioIdx: number): number {
  if (studioIdx >= 4) {
    return 4 * COL_WIDTH + MONTAGE_GAP + (studioIdx - 4) * COL_WIDTH;
  }
  return studioIdx * COL_WIDTH;
}

/** Координаты → studioIdx (0-5) или -1 если в gap */
function getStudioFromX(x: number): number {
  if (x < 4 * COL_WIDTH) {
    const idx = Math.floor(x / COL_WIDTH);
    return Math.min(idx, 3);
  }
  const afterGap = x - 4 * COL_WIDTH - MONTAGE_GAP;
  if (afterGap < 0) return -1;
  const idx = 4 + Math.floor(afterGap / COL_WIDTH);
  return Math.min(idx, 5);
}

function getSlotFromY(y: number): number {
  return Math.max(0, Math.min(Math.floor(y / SLOT_HEIGHT), TIME_SLOTS.length - 1));
}

function isSessionDrag(e: React.DragEvent): boolean {
  return e.dataTransfer?.types?.includes(SESSION_DRAG_TYPE) ?? false;
}

/** Обёртка с мемоизацией style — иначе SessionBubble перерисовывается при каждом рендере DayCell из‑за нового объекта style */
const SessionBubbleWithStyle = memo(function SessionBubbleWithStyle({
  session,
  commentCount = 0,
  scheduleSearchFilter = SCHEDULE_SEARCH_INACTIVE,
}: {
  session: Session;
  commentCount?: number;
  scheduleSearchFilter?: ScheduleSearchFilterState;
}) {
  const studioIdx = getStudioIndex(session.studio);
  const style = useMemo(() => {
    if (studioIdx >= 6) return null;
    const startMins = parseTimeToMinutes(session.start as string);
    const endMins = parseTimeToMinutes(session.end as string);
    const top = (startMins / TOTAL_MINUTES) * CONTENT_HEIGHT;
    let height = ((endMins - startMins) / TOTAL_MINUTES) * CONTENT_HEIGHT;
    if (height < 5) height = 5;
    const left = getSlotLeft(studioIdx);
    const width = COL_WIDTH;
    const visualInset = (COL_WIDTH - COL_WIDTH * SESSION_VISUAL_WIDTH_RATIO) / 2;
    return { top, left, width, height, visualInset };
  }, [studioIdx, session.start, session.end]);

  if (studioIdx >= 6 || !style) return null;
  return (
    <SessionBubble
      session={session}
      style={style}
      commentCount={commentCount}
      scheduleSearchFilter={scheduleSearchFilter}
    />
  );
});

interface DayCellProps {
  dayCell: DayCellType;
  daySessions: Session[];
  sessionCommentCountById?: Readonly<Record<string, number>>;
  scheduleSearchFilter?: ScheduleSearchFilterState;
}

function DayCellInner({ dayCell, daySessions, sessionCommentCountById, scheduleSearchFilter }: DayCellProps) {
  const { onSlotClick, onSlotDrop, onAddClick, onOpenCanceledSessionsPopover } = useScheduleHandlers();
  const { gridSelection } = useScheduleSelection();

  const [dropTarget, setDropTarget] = useState<{ studioIdx: number; slotIdx: number } | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ studioIdx: number; slotIdx: number } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [addBtnPosition, setAddBtnPosition] = useState<{ top: number; left: number } | null>(null);

  const { year, month, dayOfMonth, inMonth } = dayCell;
  const date = new Date(year, month, dayOfMonth);
  const dayOfWeek = date.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const dayName = DAY_NAMES[dayOfWeek];
  const formattedDate = `${String(dayOfMonth).padStart(2, '0')}.${String(month + 1).padStart(2, '0')}`;
  const datePart = inMonth ? formattedDate : `${formattedDate} (${MONTH_NAMES[month].toUpperCase()})`;
  const dateIso = toDayIso(dayCell);

  const hasCanceledSessions = useMemo(
    () =>
      daySessions.some(
        (s) => (s.is_canceled ?? false) && (s.canceled_hidden_from_schedule ?? false)
      ),
    [daySessions]
  );

  const visibleDaySessions = useMemo(
    () =>
      daySessions.filter(
        (s) => !(s.is_canceled && (s.canceled_hidden_from_schedule ?? false))
      ),
    [daySessions]
  );

  const getBlockingSessionAtCell = useCallback(
    (studioIdx: number, slotIdx: number, excludeSessionId = '') =>
      getSessionAtSlot(visibleDaySessions, dateIso, studioIdx, slotIdx, excludeSessionId),
    [dateIso, visibleDaySessions]
  );

  const isSelected =
    gridSelection &&
    gridSelection.date === dateIso &&
    inMonth;

  const handleSlotClick = useCallback(
    (studioIdx: number, slotIdx: number, addToSelection: boolean) => {
      onSlotClick(dateIso, studioIdx, slotIdx, addToSelection);
    },
    [dateIso, onSlotClick]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const studioIdx = getStudioFromX(x);
      if (studioIdx < 0) return;
      const slotIdx = getSlotFromY(y);
      if (getBlockingSessionAtCell(studioIdx, slotIdx)) return;
      handleSlotClick(studioIdx, slotIdx, hasSlotSelectModifier(e));
    },
    [getBlockingSessionAtCell, handleSlotClick]
  );

  const handleOverlayDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!isSessionDrag(e)) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const studioIdx = getStudioFromX(x);
      if (studioIdx < 0) {
        setDropTarget(null);
        return;
      }
      const slotIdx = getSlotFromY(y);
      const excludeSessionId = scheduleDragState.isRecorded ? '' : (scheduleDragState.sessionId ?? '');
      const blockingSession = getBlockingSessionAtCell(studioIdx, slotIdx, excludeSessionId);
      e.preventDefault();
      const dropEffect = getSessionDropEffect(e, scheduleDragState.isRecorded);
      if (dropEffect) e.dataTransfer.dropEffect = dropEffect;
      setDropTarget((prev) => {
        if (blockingSession) return prev === null ? prev : null;
        return prev?.studioIdx === studioIdx && prev?.slotIdx === slotIdx ? prev : { studioIdx, slotIdx };
      });
    },
    [getBlockingSessionAtCell]
  );

  const handleOverlayDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleOverlayDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const studioIdx = getStudioFromX(x);
      if (studioIdx >= 0) {
        const slotIdx = getSlotFromY(y);
        onSlotDrop(dateIso, studioIdx, slotIdx, e);
      }
      setDropTarget(null);
    },
    [dateIso, onSlotDrop]
  );

  const handleOverlayMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const studioIdx = getStudioFromX(x);
      if (studioIdx < 0) {
        setHoveredCell(null);
        return;
      }
      const slotIdx = getSlotFromY(y);
      if (getBlockingSessionAtCell(studioIdx, slotIdx)) {
        setHoveredCell(null);
        return;
      }
      setHoveredCell((prev) =>
        prev?.studioIdx === studioIdx && prev?.slotIdx === slotIdx ? prev : { studioIdx, slotIdx }
      );
    },
    [getBlockingSessionAtCell]
  );

  const handleOverlayMouseLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);

  const slotIdx = gridSelection?.slotIdx ?? 0;
  const endSlotIdx = gridSelection?.endSlotIdx ?? gridSelection?.slotIdx ?? 0;
  const [startSlotIdx, endSlotIdxSorted] = slotIdx <= endSlotIdx ? [slotIdx, endSlotIdx] : [endSlotIdx, slotIdx];
  const timeRangeStr = isSelected && gridSelection
    ? `${TIME_SLOTS[startSlotIdx].split(' - ')[0]} - ${TIME_SLOTS[endSlotIdxSorted].split(' - ')[1]}`
    : '';

  const showAddBtn = Boolean(isSelected && gridSelection && onAddClick);

  const updateAddBtnPosition = useCallback(() => {
    if (!showAddBtn || !contentRef.current || !gridSelection) return;
    const rect = contentRef.current.getBoundingClientRect();
    setAddBtnPosition({
      top: rect.top + startSlotIdx * SLOT_HEIGHT - 35,
      left: rect.left + TIME_COLUMN_WIDTH + getSlotLeft(gridSelection.studioIdx) + COL_WIDTH / 2,
    });
  }, [showAddBtn, gridSelection, startSlotIdx]);

  useLayoutEffect(() => {
    if (!showAddBtn) {
      setAddBtnPosition(null);
      return;
    }
    updateAddBtnPosition();
  }, [showAddBtn, updateAddBtnPosition]);

  useEffect(() => {
    if (!showAddBtn) return;
    const scrollEl = document.querySelector('.schedule-page__scroll');
    let rafId: number | null = null;
    const onScrollOrResize = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateAddBtnPosition();
      });
    };
    scrollEl?.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      scrollEl?.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showAddBtn, updateAddBtnPosition]);

  const addBtnPortal =
    showAddBtn &&
    addBtnPosition &&
    createPortal(
      <button
        type="button"
        className="schedule-day-cell__add-btn schedule-day-cell__add-btn_portal"
        style={{
          top: addBtnPosition.top,
          left: addBtnPosition.left,
        }}
        onClick={() => onAddClick!()}
        aria-label="Добавить сессию"
      >
        <AddIcon size={16} className="schedule-day-cell__add-btn-icon" />
        Добавить запись
      </button>,
      document.body
    );

  return (
    <div
      className={`schedule-day-cell ${inMonth ? 'schedule-day-cell_in-month' : ''} ${isWeekend ? 'schedule-day-cell_weekend' : ''}`}
    >
      <div className="schedule-day-cell__header schedule-day-cell__header--three-cols">
        <div className="schedule-day-cell__header-col schedule-day-cell__header-col--left" aria-hidden="true" />
        <div className="schedule-day-cell__header-col schedule-day-cell__header-col--center">
          <span className="schedule-day-cell__day-name">{dayName}</span>
          <span className="schedule-day-cell__sep">{'//'}</span>
          <span className="schedule-day-cell__date">{datePart}</span>
        </div>
        <div className="schedule-day-cell__header-col schedule-day-cell__header-col--right">
          {hasCanceledSessions ? (
            <button
              type="button"
              className="schedule-day-cell__canceled-btn"
              onClick={(e) => onOpenCanceledSessionsPopover(dateIso, e.currentTarget)}
            >
              Отменённые сессии
            </button>
          ) : null}
        </div>
      </div>
      <div className="schedule-day-cell__studios">
        <div className="schedule-day-cell__time-col" />
        <div className="schedule-day-cell__studios-row">
          {STUDIOS.slice(0, 4).map((name) => (
            <div key={name} className="schedule-day-cell__studio">
              {name}
            </div>
          ))}
          <div className="schedule-day-cell__studio-gap" />
          {STUDIOS.slice(4, 6).map((name) => (
            <div key={name} className="schedule-day-cell__studio schedule-day-cell__studio_montage">
              {name}
            </div>
          ))}
        </div>
      </div>
      <div ref={contentRef} className="schedule-day-cell__content">
        <div className="schedule-day-cell__timeline">
          {TIME_SLOTS.map((slot, i) => (
            <div
              key={slot}
              className={`schedule-day-cell__slot ${isWeekend ? 'schedule-day-cell__slot_weekend' : ''}`}
              style={{ top: i * SLOT_HEIGHT }}
            >
              {slot}
            </div>
          ))}
        </div>
        <div className="schedule-day-cell__montage-label" aria-hidden>
          <span>М</span><span>О</span><span>Н</span><span>Т</span><span>А</span><span>Ж</span>
        </div>
        <div className="schedule-day-cell__slots-grid">
          <div className="schedule-day-cell__slots-grid-lines" aria-hidden>
            {TIME_SLOTS.map((_, slotI) =>
              STUDIOS.map((_, studioI) => (
                <div
                  key={`${studioI}-${slotI}`}
                  className={`schedule-day-cell__slot-cell schedule-day-cell__slot-cell_line ${isWeekend ? 'schedule-day-cell__slot-cell_weekend' : ''}`}
                  style={{
                    top: slotI * SLOT_HEIGHT,
                    left: getSlotLeft(studioI),
                    width: COL_WIDTH,
                    height: SLOT_HEIGHT,
                  }}
                />
              ))
            )}
          </div>
          <div
            className="schedule-day-cell__slots-overlay"
            onClick={handleOverlayClick}
            onMouseMove={handleOverlayMouseMove}
            onMouseLeave={handleOverlayMouseLeave}
            onDragOver={handleOverlayDragOver}
            onDragEnter={handleOverlayDragOver}
            onDragLeave={handleOverlayDragLeave}
            onDrop={handleOverlayDrop}
          >
            {hoveredCell && !dropTarget && (
              <div
                className={`schedule-day-cell__slot-cell schedule-day-cell__slot-cell_hover ${isWeekend ? 'schedule-day-cell__slot-cell_weekend' : ''}`}
                style={{
                  top: hoveredCell.slotIdx * SLOT_HEIGHT,
                  left: getSlotLeft(hoveredCell.studioIdx),
                  width: COL_WIDTH,
                  height: SLOT_HEIGHT,
                }}
                aria-hidden
              />
            )}
            {dropTarget && (
              <div
                className={`schedule-day-cell__slot-cell schedule-day-cell__slot-cell_drop-target ${isWeekend ? 'schedule-day-cell__slot-cell_weekend' : ''}`}
                style={{
                  top: dropTarget.slotIdx * SLOT_HEIGHT,
                  left: getSlotLeft(dropTarget.studioIdx),
                  width: COL_WIDTH,
                  height: SLOT_HEIGHT,
                }}
                aria-hidden
              />
            )}
          </div>
        </div>
        {isSelected && gridSelection && (
          <>
            <div
              className="schedule-day-cell__selection-overlay"
              style={{
                top: startSlotIdx * SLOT_HEIGHT,
                left: TIME_COLUMN_WIDTH + getSlotLeft(gridSelection.studioIdx),
                width: COL_WIDTH,
                height: (endSlotIdxSorted - startSlotIdx + 1) * SLOT_HEIGHT,
              }}
            >
              <span className="schedule-day-cell__selection-time">{timeRangeStr}</span>
            </div>
          </>
        )}
        <div className="schedule-day-cell__grid">
          {visibleDaySessions.map((session) => (
            <SessionBubbleWithStyle
              key={session.id}
              session={session}
              commentCount={sessionCommentCountById?.[session.id] ?? 0}
              scheduleSearchFilter={scheduleSearchFilter ?? SCHEDULE_SEARCH_INACTIVE}
            />
          ))}
        </div>
      </div>
      {addBtnPortal}
    </div>
  );
}

export const DayCell = memo(DayCellInner);
